import { plural, t } from 'utils/i18n';
import type { FrameStatus } from '../steps/steps-api';

// the attributes that hold a library choice; blend_model wins when a label has both
export const MODEL_SPEC_NAMES = ['blend_model', 'material'];
export const FIND_CHUNK = 5;

export interface Candidate {
    id?: number;
    name: string;
    path: string;
    image_name?: string;
    image_path?: string;
}

export interface Detection {
    clientID: number;
    serverID: number | null;
    frame: number;
    labelName: string;
    labelColor: string;
    hidden: boolean;
    lock: boolean;
    specId: number;
    value: string;
    candidates: Candidate[];
    points: number[];
    shapeType: string;
}

export interface ModelView {
    name: string;
    imageUrl: string | null;
    fullImageUrl: string | null;
    candidate: Candidate | null;
}

interface LabelLike {
    id: number;
    name: string;
    color?: string;
    attributes: { id: number; name: string }[];
}

export interface StateLike {
    objectType: string;
    clientID: number;
    serverID?: number | null;
    frame: number;
    hidden?: boolean;
    lock?: boolean;
    label: LabelLike;
    attributes: Record<number, string>;
    select_values?: Record<number, unknown>;
    points?: number[];
    shapeType?: string;
}

export function modelSpecOf(label: LabelLike): { id: number; name: string } | null {
    for (const name of MODEL_SPEC_NAMES) {
        const spec = label.attributes.find((attr) => attr.name === name);
        if (spec) return spec;
    }
    return null;
}

export function parseCandidates(raw: unknown): Candidate[] {
    let value = raw;
    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is Candidate => !!item &&
        typeof item.name === 'string' && typeof item.path === 'string');
}

export function detectionOf(state: StateLike): Detection | null {
    if (state.objectType !== 'shape') return null;
    const spec = modelSpecOf(state.label);
    if (!spec) return null;
    return {
        clientID: state.clientID,
        serverID: typeof state.serverID === 'number' ? state.serverID : null,
        frame: state.frame,
        labelName: state.label.name,
        labelColor: state.label.color ?? '#ddd',
        hidden: !!state.hidden,
        lock: !!state.lock,
        specId: spec.id,
        value: (state.attributes[spec.id] ?? '').trim(),
        candidates: parseCandidates(state.select_values?.[spec.id]),
        points: state.points ?? [],
        shapeType: state.shapeType ?? 'rectangle',
    };
}

const SHAPE_SUFFIX = /-shape\d+$/;

function baseName(path: string): string {
    return path.split(/[\\/]/).pop() || path;
}

export function displayName(name: string): string {
    return name.replace(/\.blend$/i, '');
}

export function previewUrl(imagePath: string | undefined, backendAPI: string, full = false): string | null {
    return imagePath ? `${backendAPI}/library_preview/${imagePath}${full ? '?full=1' : ''}` : null;
}

export function resolveModel(
    detection: Pick<Detection, 'value' | 'candidates'>, backendAPI: string,
): ModelView | null {
    const { value, candidates } = detection;
    if (!value) return null;
    // values come as a full path, a file name or "<file>-shape<id>" depending on who wrote them
    const plain = value.replace(SHAPE_SUFFIX, '');
    const candidate = candidates.find((item) => item.path === value) ??
        candidates.find((item) => item.name === value || item.name === plain) ??
        candidates.find((item) => baseName(item.path) === baseName(plain)) ?? null;
    if (candidate) {
        return {
            name: displayName(candidate.name),
            imageUrl: previewUrl(candidate.image_path, backendAPI),
            fullImageUrl: previewUrl(candidate.image_path, backendAPI, true),
            candidate,
        };
    }
    return {
        name: displayName(baseName(plain)), imageUrl: null, fullImageUrl: null, candidate: null,
    };
}

export function filterByLabel(detections: Detection[], query: string): Detection[] {
    const needle = query.trim().toLowerCase();
    return needle ? detections.filter((item) => item.labelName.toLowerCase().includes(needle)) : detections;
}

// categories in order of first appearance; the items keep their frame order
export function groupByLabel(detections: Detection[]): { label: string; items: Detection[] }[] {
    const groups: { label: string; items: Detection[] }[] = [];
    for (const item of detections) {
        const group = groups.find((entry) => entry.label === item.labelName);
        if (group) group.items.push(item);
        else groups.push({ label: item.labelName, items: [item] });
    }
    return groups;
}

export function summarize(detections: Detection[]): { total: number; withModel: number; missing: Detection[] } {
    const missing = detections.filter((item) => !item.value);
    return { total: detections.length, withModel: detections.length - missing.length, missing };
}

export function frameStatuses(detections: Detection[]): Record<number, FrameStatus> {
    const byFrame: Record<number, { total: number; withModel: number }> = {};
    for (const item of detections) {
        const entry = byFrame[item.frame] ?? { total: 0, withModel: 0 };
        entry.total += 1;
        if (item.value) entry.withModel += 1;
        byFrame[item.frame] = entry;
    }
    const result: Record<number, FrameStatus> = {};
    for (const [frame, { total, withModel }] of Object.entries(byFrame)) {
        if (withModel === total) result[+frame] = 'done';
        else result[+frame] = withModel ? 'partial' : 'pending';
    }
    return result;
}

export function chunk<T>(items: T[], size: number): T[][] {
    const parts: T[][] = [];
    for (let i = 0; i < items.length; i += size) parts.push(items.slice(i, i + size));
    return parts;
}

export function findLabel(count: number): string {
    return t('Find models for {count}', { count: plural(count, 'item') });
}

export function coverageLabel(withModel: number, total: number): string {
    return t('{done} of {total} have a model from the library', { done: withModel, total });
}
